import type { IProposal } from '@shared/models/proposals.model';
import type { Baseline } from '@shared/models/project.model';

export type ReportPageHeaderId = 'productGroups' | 'topProducts' | 'actions';

export type ReportPageHeaderSource = Pick<
  IProposal & Baseline,
  | 'report_title'
  | 'report_description'
  | 'report_product_groups_title'
  | 'report_product_groups_description'
  | 'report_top_products_title'
  | 'report_top_products_description'
  | 'report_actions_title'
  | 'report_actions_description'
>;

const REPORT_PAGE_TITLE_FIELD = {
  productGroups: 'report_product_groups_title',
  topProducts: 'report_top_products_title',
  actions: 'report_actions_title',
} as const satisfies Record<ReportPageHeaderId, keyof ReportPageHeaderSource>;

const REPORT_PAGE_DESCRIPTION_FIELD = {
  productGroups: 'report_product_groups_description',
  topProducts: 'report_top_products_description',
  actions: 'report_actions_description',
} as const satisfies Record<ReportPageHeaderId, keyof ReportPageHeaderSource>;

/** Persisted hero title for one report slide. */
export const getReportPageTitle = (
  source: ReportPageHeaderSource,
  pageId: ReportPageHeaderId,
): string | undefined => {
  const value = source[REPORT_PAGE_TITLE_FIELD[pageId]];
  if (value !== undefined) return value;
  if (pageId === 'productGroups') return source.report_title;
  return undefined;
};

/** Persisted hero body for one report slide. */
export const getReportPageDescription = (
  source: ReportPageHeaderSource,
  pageId: ReportPageHeaderId,
): string | undefined => {
  const value = source[REPORT_PAGE_DESCRIPTION_FIELD[pageId]];
  if (value !== undefined) return value;
  if (pageId === 'productGroups') return source.report_description;
  return undefined;
};

/** Partial update for one slide's hero title. */
export const buildReportPageTitleUpdate = (
  pageId: ReportPageHeaderId,
  next: string,
): Partial<ReportPageHeaderSource> => ({
  [REPORT_PAGE_TITLE_FIELD[pageId]]: next ? next : undefined,
});

export type ResolveReportPrintBodyTextOptions = {
  /** When false, an empty body is omitted instead of showing the editor placeholder. */
  showPlaceholder: boolean;
};

/**
 * Body copy for print/PDF.
 * Saved text wins. Editor prompts such as "Add a short description…" are omitted
 * unless {@link ResolveReportPrintBodyTextOptions.showPlaceholder} is set.
 */
export const resolveReportPrintBodyText = (
  text: string | undefined,
  placeholder: string | undefined,
  options: ResolveReportPrintBodyTextOptions,
): string | undefined => {
  const trimmed = text?.trim();
  if (trimmed) return trimmed;
  if (!options.showPlaceholder) return undefined;
  const trimmedPlaceholder = placeholder?.trim();
  return trimmedPlaceholder || undefined;
};

/** Partial update for one slide's hero body. */
export const buildReportPageDescriptionUpdate = (
  pageId: ReportPageHeaderId,
  next: string,
): Partial<ReportPageHeaderSource> => ({
  [REPORT_PAGE_DESCRIPTION_FIELD[pageId]]: next ? next : undefined,
});
